import { log, error, parseBinary, updateBarSubscribers } from './helpers';
import { updateQuoteSubscribers } from './quotes';
import store from './store';
import { SocketMessage } from '../types';
import { CODE, MODE } from '../constants';

const SOCKET_CONFIG = {
  sendHBInterval: 10000,
  disconDetectionInterval: 3000,
  reconnectDelay: 2000,
} as const;

// -------------------------------------------------
// socket endpoint
const SOCKET_URL = process.env.SOCKET_URL;
if (!SOCKET_URL) {
  throw new Error('SOCKET_URL is not defined');
}

const url = `${SOCKET_URL}?session=${store.getAuthSession()}&token=${store.getAuthToken()}`;

let socket: WebSocket | null = null;

let heartbeatSendTimeout: ReturnType<typeof setTimeout> | null = null;
let heartbeatResponseTimeout: ReturnType<typeof setTimeout> | null = null;
let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
let reconnectScheduled = false;

let pendingQueue: (string | SocketMessage)[] = [];

function clearHeartbeatTimers() {
  if (heartbeatSendTimeout) {
    clearTimeout(heartbeatSendTimeout);
    heartbeatSendTimeout = null;
  }
  if (heartbeatResponseTimeout) {
    clearTimeout(heartbeatResponseTimeout);
    heartbeatResponseTimeout = null;
  }
}

function closeSocketConnection() {
  clearHeartbeatTimers();

  if (socket) {
    socket.onopen = null;
    socket.onerror = null;
    socket.onclose = null;
    socket.onmessage = null;
    socket.close();
  }
  socket = null;
}

// ConnectSocket -------------------------------
export const connectSocket = () => {
  if (
    store.isSocketDisabled() ||
    (socket &&
      (socket.readyState === WebSocket.CONNECTING ||
        socket.readyState === WebSocket.OPEN))
  ) {
    return;
  }

  const ws = new WebSocket(url);
  socket = ws;
  ws.onopen = onOpen;
  ws.onerror = onError;
  ws.onclose = onClose;
  ws.onmessage = onMessage;
};

// onOpen ---------------------------------------
const onOpen = () => {
  log('🟢 Connected to socket ');
  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = null;
  }
  reconnectScheduled = false;
  clearHeartbeatTimers();
  startHeartbeating();
  subscribePreviousSubscriptions();
  processPendingRequests();
};

// onError -------------------------------------------
const onError = (event: Event) => {
  error('⛔ Error while connection to socket', event);
  reconnect();
};

// onClose -------------------------------------------
const onClose = (event: CloseEvent) => {
  log('🔴 Socket connection closed.', event);
  reconnect();
};

// onMessage -----------------------------------------
const onMessage = (event: MessageEvent) => {
  log('⬇ Received message:', event.data);

  heartbeatResponseReceived();

  if (event.data instanceof Blob) {
    const reader = new FileReader();
    reader.onload = () => {
      // Parse the binary data
      const tick = parseBinary(reader.result as ArrayBuffer);
      log('⬇ Received message:', tick);
      // update all listeners
      updateBarSubscribers(tick);
      updateQuoteSubscribers(tick);
    };
    reader.onerror = () => {
      console.error('Error reading binary data.');
    };
    reader.readAsArrayBuffer(event.data);
  } else {
    log('⬇🫤 Unknown message received:', event.data);
  }
};

/* Emit ------------------------------------
 Emits messages to socket server. If the connection is Open, will be sent immediately,
 else will be queued and sent once connected.
 Incase do want want to queue a request. set volatile to true.
*/
export const emit = (
  message: string | SocketMessage,
  volatile: boolean = false,
) => {
  message = typeof message === 'string' ? message : JSON.stringify(message);

  if (socket?.readyState === WebSocket.OPEN) {
    // emit only if connection open
    log('⬆ Emitted to socket: ', message);
    socket.send(message);
  } else {
    // put request to queue
    error(
      `❗ Error emitting to socket because socket state is: ${
        socket?.readyState ?? 'no socket'
      }. ${!volatile ? 'Request queued until reconnection' : ''}`,
    );
    if (!volatile) {
      pendingQueue.push(message);
    }
  }
};

/* Reconnect ----------------------------------------------
  Closes the current socket and opens a new connection after a short delay.
*/
const reconnect = () => {
  if (reconnectScheduled) {
    log('🙈 Reconnect request ignored. Already scheduled.');
    return;
  }

  reconnectScheduled = true;
  log('🔄 Scheduling socket reconnect…');
  closeSocketConnection();

  reconnectTimeout = setTimeout(() => {
    reconnectTimeout = null;
    reconnectScheduled = false;
    connectSocket();
  }, SOCKET_CONFIG.reconnectDelay);
};

const startHeartbeating = () => {
  if (heartbeatSendTimeout) clearTimeout(heartbeatSendTimeout);
  heartbeatSendTimeout = setTimeout(() => {
    heartbeatSendTimeout = null;
    waitForHeartbeatResponse(SOCKET_CONFIG.disconDetectionInterval);
  }, SOCKET_CONFIG.sendHBInterval);
};

const waitForHeartbeatResponse = (waitDuration: number) => {
  if (heartbeatResponseTimeout) clearTimeout(heartbeatResponseTimeout);
  heartbeatResponseTimeout = setTimeout(reconnectOnHBFailed, waitDuration);
};

const reconnectOnHBFailed = () => {
  heartbeatResponseTimeout = null;
  log('🆚 Heartbeat / liveness window elapsed without traffic. Reconnecting…');
  reconnect();
};

const heartbeatResponseReceived = () => {
  if (heartbeatResponseTimeout) {
    clearTimeout(heartbeatResponseTimeout);
    heartbeatResponseTimeout = null;
  }
  if (heartbeatSendTimeout) {
    clearTimeout(heartbeatSendTimeout);
    heartbeatSendTimeout = null;
  }
  startHeartbeating();
};

/*
  ProcessPendingRequests --------------------------------------
  !TO BE ONLY CALLED IN "onOpen" METHOD OF WEBSOCKET
  Processes requests which were not processed due to socket state other than OPEN.
*/
const processPendingRequests = () => {
  // Process pending requests.
  if (pendingQueue.length > 0) {
    log('⏳ Processing pending requests...');

    const pendingQueueCopy = pendingQueue;
    pendingQueue = []; // init orignal with fresh

    pendingQueueCopy.forEach((request) => {
      emit(request);
    });
  }
};

/* subscribePreviousSubscriptions --------------------------------
  !TO BE ONLY CALLED IN "onOpen" METHOD OF WEBSOCKET
  Emits subscription requests in store to socket.
  Helpful if socket abruptly disconnects.
*/
const subscribePreviousSubscriptions = () => {
  log('⏳ Processing previous subscriptions if any...');
  for (const token of store.getAllSocketSubscriptions().keys()) {
    emit({
      code: CODE.SUB,
      mode: MODE.FULL,
      full: [token],
    });
  }
};
